import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import createDecorator from 'final-form-calculate';
import { Form } from 'react-final-form';
import { useQueryClient } from 'react-query';
import { useRecoilValue } from 'recoil';
import styled from 'styled-components';
import moment from 'moment';
import { ConfirmationModal, FormModal as BaseFormModal } from '@ubeya/shared-web/components';
import { useModal } from '@ubeya/shared-web/components/Modal';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import PrivilegesContext from '@ubeya/shared-web/contexts/PrivilegesContext';
import privileges from '@ubeya/shared-web/privileges';
import { media } from '@ubeya/shared-web/style';
import { formatPhone } from '@ubeya/shared-web/utils/employee';
import useAccount from '@ubeya/shared/hooks/account/useAccount';
import useBranches from '@ubeya/shared/hooks/account/useBranches';
import useModules from '@ubeya/shared/hooks/account/useModules';
import usePayrollFields from '@ubeya/shared/hooks/account/usePayrollFields';
import useUser from '@ubeya/shared/hooks/auth/useUser';
import useEmployee from '@ubeya/shared/hooks/employees/useEmployee';
import useEmployeeContracts from '@ubeya/shared/hooks/employees/useEmployeeContracts';
import useToaster from '@ubeya/shared/hooks/useToaster';
import useFormattingMap from '@ubeya/shared-web/hooks/useFormattingMap';
import { formatFieldsArrayToMap, getDirtyFields } from '@ubeya/shared/utils/form';
import { ACCOUNTS_CONTRACTS_TYPES, API_DATE_FORMAT } from '@ubeya/shared/constants';
import { ACCOUNTS_REQUIRING_ASSIGNED_CONTRACT_ON_EMPLOYEE_CREATE } from '@ubeya/shared/utils/accounts';
import useContractAccess from '@ubeya/shared/hooks/account/useContractAccess';
import { selectedBookingModal } from 'shared/atoms/scheduling';
import CardTitle from './CardTitle';
import Documents from './Documents/Documents';
import Job from './Job/Job';
import PersonalSettings from './PersonalSettings/PersonalSettings';
import Profile from './Profile/Profile';
import TabContent from './TabContent';
import Tabs from './Tabs';
import Wages from './Wages/Wages';
import EmployeeAuditLogs from './EmployeeAuditLogs/EmployeeAuditLogs';
import EmployeeAvailability from './EmployeeAvailability/EmployeeAvailability';
import EmployeeComment from './EmployeeComment/EmployeeComment';
import EmployeeCompliance from './EmployeeCompliance/EmployeeCompliance';
import EmployeeGeneralAvailability from './EmployeeGeneralAvailability/EmployeeGeneralAvailability';
import { ProfileSidebar } from './ProfileSidebar';
import EmployeeAWR from './EmployeeAWR/EmployeeAWR';
import EmployeeWorkingTimeDirective from './EmployeeWorkingTimeDirective/EmployeeWorkingTimeDirective';
import EmployeeContracts from './EmployeeContracts/EmployeeContracts';
import LeaveManagement from './LeaveManagement/LeaveManagement';
import EmployeeLeaveManagementV2 from './EmployeeLeaveManagementV2/EmployeeLeaveManagementV2';
import ApplicationsAndRejections from './ApplicationsAndRejections/ApplicationsAndRejections';
import EmployeeHiring from './EmployeeHiring/EmployeeHiring';
import TicketsHistorySection from 'helpDesk/components/TicketsHistorySection';
import {
  MINT_ACCOUNT_EMPLOYEE_TYPE_FIELD_ID,
  MINT_ACCOUNT_EMPLOYEE_TYPE_TEMP_ID,
  MINT_ACCOUNT_ID
} from 'employees/constants';

const TicketsHistoryWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 40px;
  padding-top: 0;
  overflow-y: auto;
`;

const FormModal = styled(BaseFormModal)`
  > .title {
    padding: 15px 30px;
    ${media.tablet`padding-left: 260px; border:none;`}
  }

  > .content {
    border-top: ${({ theme }) => `1px solid ${theme.colors.grey2}`};
    ${media.tablet`padding-left: 260px;`}
  }
`;

const getConfigByBranch = (branches, selectedBranches = []) => {
  const selectedBranch = branches.find(({ id }) => selectedBranches.includes(id));
  return selectedBranch?.employeeConfig;
};

const getDecorator = (branches) =>
  createDecorator(
    {
      field: 'isShift',
      updates: { isShift: (isShift, { isDaily }) => (isShift ? true : !isDaily) }
    },
    {
      field: 'isDaily',
      updates: { isDaily: (isDaily, { isShift }) => (isDaily ? true : !isShift) }
    },
    {
      field: 'branches',
      updates: {
        isDaily: (selectedBranches, { id, isDaily }) => {
          if (id) {
            return isDaily;
          }
          return getConfigByBranch(branches, selectedBranches)?.isDaily;
        }
      }
    },
    {
      field: 'branches',
      updates: {
        isShift: (selectedBranches, { id, isShift }) => {
          if (id) {
            return isShift;
          }
          return getConfigByBranch(branches, selectedBranches)?.isShift;
        }
      }
    }
  );

const EmployeeCard = ({
  defaultData,
  defaultActiveTab = 'profile',
  onClose,
  editProfile = false,
  addProfile = false,
  onSubmit = undefined,
  refetchPayroll = false,
  modalSize = 'xxlarge',
  hideCancelButton = false,
  onEditClickFromSideBar,
  zIndex
}) => {
  const [showEditProfile, setShowEditProfile] = useState(!defaultData?.id || editProfile);
  const [activeTab, setActiveTab] = useState(defaultActiveTab);
  const [showEscapeConfirm, setShowEscapeConfirm] = useState(false);

  const pristineRef = useRef(true);
  const myStackIndexRef = useRef(null);
  const { modalCallbacks } = useModal();

  const {
    accountId,
    account: { weeklyPreference, recurringAvailability }
  } = useAccount();
  const queryClient = useQueryClient();

  const mode = addProfile || !defaultData ? 'ADD' : 'EDIT';

  const employeeId = defaultData?.id;

  const { isLoading, data, updateEmployee, isLoadingUpdateEmployee, inviteEmployee, deleteEmployee } = useEmployee({
    employeeId
  });

  const { assignContract, adjustEmployeeContract: adjustContract } = useEmployeeContracts({ showToasts: false });

  const [isProcessingContract, setIsProcessingContract] = useState(false);

  const { branchesOptions, branches } = useBranches();
  const { addError, addResponseError, addSuccess } = useToaster();
  const { t } = useContext(ConfigContext);
  const myPrivileges = useContext(PrivilegesContext);
  const { modules } = useModules();
  const { enableContractFeature, enableContractLeaveManagementFeature } = useContractAccess();
  const formattingMap = useFormattingMap();

  const { fields: allFields } = usePayrollFields();

  const { isUbeyaAdmin, accounts = [] } = useUser();

  const employeePayrollFields = useMemo(
    () => data?.payrollFields.reduce((all, field) => ({ ...all, [field.id]: field.value }), {}),
    [data?.payrollFields]
  );

  const employeeWages = useMemo(
    () =>
      allFields
        .filter(({ deletedAt }) => !deletedAt)
        .map(({ slug, id, value: generalValue }) => ({
          id,
          slug,
          value:
            generalValue === employeePayrollFields?.[id] || employeePayrollFields?.[id] === 0
              ? ''
              : employeePayrollFields?.[id],
          defaultValue: generalValue
        })),
    [allFields, employeePayrollFields]
  );

  const accountRequiresContract =
    accountId != null && ACCOUNTS_REQUIRING_ASSIGNED_CONTRACT_ON_EMPLOYEE_CREATE.includes(Number(accountId));

  const handleSubmit = useCallback(
    async (values, form) => {
      if (mode === 'ADD' && accountRequiresContract && !values.contractId) {
        addError(t('missingRequiredAssignedContractOnly'));
        return { contractId: 'required' };
      }

      const { dirtyFields } = form.getState();

      const dirtyValues = getDirtyFields(values, {
        ...dirtyFields,
        isShift: true,
        isDaily: true,
        branches: branchesOptions.length === 1 ? true : dirtyFields.branches,
        fields: false,
        privilegesMap: false,
        preferredClients: [],
        blockedClients: [],
        // Exclude contract fields - they're handled separately in contract assignment flow
        contractId: false,
        contractStartDate: false,
        contractMinimumWeeklyWorkingHours: false,
        contractMinimumHourlyRateOfPay: false,
        contractHolidayAccrualYearlyEntitlement: false,
        contractMaxDaysConsumeInWeek: false,
        contractSickDaysYearlyEntitlement: false
      });

      const dirtyWages = !!dirtyValues?.payrollFields;

      const employeeData = {
        /* In case the modal needs to be in add state, but with initial values,
        default data will contain those values, that can be changed
        */

        ...(addProfile ? defaultData : {}),
        ...dirtyValues,
        workDays: Object.keys(dirtyFields).includes('workDays') ? values.workDays : undefined,
        primaryPositionIds: Array.isArray(values.primaryPositionIds) ? values.primaryPositionIds : []
      };

      try {
        const employeeResult = await updateEmployee({ ...employeeData, phone: formatPhone(employeeData?.phone) });
        if (dirtyWages && refetchPayroll) {
          queryClient.invalidateQueries(['payroll', accountId]);
          queryClient.invalidateQueries(['timesheet', accountId]);
        }

        if (mode === 'ADD' && values.contractId && employeeResult?.data?.id) {
          setIsProcessingContract(true);
          try {
            const assignResponse = await assignContract({
              employeeId: employeeResult.data.id,
              contractId: values.contractId,
              startDate: values.contractStartDate
            });

            const hasAdjustments =
              dirtyFields.contractMinimumWeeklyWorkingHours ||
              dirtyFields.contractMinimumHourlyRateOfPay ||
              dirtyFields.contractHolidayAccrualYearlyEntitlement ||
              dirtyFields.contractMaxDaysConsumeInWeek ||
              dirtyFields.contractSickDaysYearlyEntitlement;

            if (hasAdjustments && assignResponse?.data?.id) {
              const contractAdjustmentDirtyFields = {
                contractMinimumWeeklyWorkingHours: dirtyFields.contractMinimumWeeklyWorkingHours,
                contractMinimumHourlyRateOfPay: dirtyFields.contractMinimumHourlyRateOfPay,
                contractHolidayAccrualYearlyEntitlement: dirtyFields.contractHolidayAccrualYearlyEntitlement,
                contractMaxDaysConsumeInWeek: dirtyFields.contractMaxDaysConsumeInWeek,
                contractSickDaysYearlyEntitlement: dirtyFields.contractSickDaysYearlyEntitlement
              };

              const dirtyContractValues = getDirtyFields(values, contractAdjustmentDirtyFields);

              if (dirtyContractValues && Object.keys(dirtyContractValues).length > 0) {
                const adjustmentData = {};

                if (dirtyContractValues.contractMinimumWeeklyWorkingHours) {
                  adjustmentData.minimumWeeklyWorkingHours = Number(
                    dirtyContractValues.contractMinimumWeeklyWorkingHours
                  );
                }
                if (dirtyContractValues.contractMinimumHourlyRateOfPay) {
                  adjustmentData.minimumHourlyRateOfPay = Number(dirtyContractValues.contractMinimumHourlyRateOfPay);
                }
                if (dirtyContractValues.contractHolidayAccrualYearlyEntitlement) {
                  const proratedValue = assignResponse.data?.holidayAccrualYearlyEntitlement || 0;
                  const userValue = Number(dirtyContractValues.contractHolidayAccrualYearlyEntitlement);
                  const value = userValue - proratedValue;
                  adjustmentData.holidayAccrualYearlyEntitlementAdjustment = +formattingMap.fixedNumber(value, 2);
                }
                if (dirtyContractValues.contractMaxDaysConsumeInWeek) {
                  adjustmentData.maxDaysConsumeInWeek = Number(dirtyContractValues.contractMaxDaysConsumeInWeek);
                }
                if (dirtyContractValues.contractSickDaysYearlyEntitlement) {
                  const proratedValue = assignResponse.data?.sickDaysYearlyEntitlement || 0;
                  const userValue = Number(dirtyContractValues.contractSickDaysYearlyEntitlement);
                  const value = userValue - proratedValue;
                  adjustmentData.sickDaysYearlyEntitlementAdjustment = +formattingMap.fixedNumber(value, 2);
                }

                await adjustContract({
                  employeeId: employeeResult.data.id,
                  employeeContractId: assignResponse.data.id,
                  ...adjustmentData
                });
              }
            }

            // Contract assigned successfully
            addSuccess(t('contract_assigned_successfully'));
          } catch (contractError) {
            addResponseError({
              message: t('contractAssignmentFailedOnAddEmployee')
            });
          } finally {
            setIsProcessingContract(false);
          }
        }

        if (!!employeeResult && !!onSubmit) {
          onSubmit(employeeResult?.data);
        }

        onClose();
      } catch (err) {
        addResponseError(err);
      }
    },
    [
      branchesOptions.length,
      addProfile,
      defaultData,
      updateEmployee,
      refetchPayroll,
      onSubmit,
      onClose,
      queryClient,
      accountId,
      accountRequiresContract,
      addError,
      addResponseError,
      addSuccess,
      mode,
      t,
      assignContract,
      adjustContract,
      formattingMap
    ]
  );

  const initialFieldsValue = useMemo(() => {
    const mappedFields = formatFieldsArrayToMap(data, employeeWages);
    if (accountId !== MINT_ACCOUNT_ID || mode === 'EDIT') {
      return mappedFields;
    }
    return {
      ...mappedFields,
      fields: {
        ...mappedFields?.fields,
        [`field-${MINT_ACCOUNT_EMPLOYEE_TYPE_FIELD_ID}`]: MINT_ACCOUNT_EMPLOYEE_TYPE_TEMP_ID
      }
    };
  }, [accountId, data, mode, employeeWages]);

  const initialValues = useMemo(() => {
    const workDays = branches.map(({ id, timesheetConfig }) => {
      const customDays = data?.workDays?.find?.(({ branchId }) => branchId === id)?.days;
      return { branchId: id, days: customDays || timesheetConfig?.workDays };
    });

    // BE exposes primary positions as either a flat list or via accountsPositions[].isPrimary.
    const primaryPositionIds = (() => {
      if (Array.isArray(data?.primaryPositionIds)) return data.primaryPositionIds;
      if (Array.isArray(data?.accountsPositions)) {
        return data.accountsPositions.filter(({ isPrimary }) => isPrimary).map(({ positionId }) => positionId);
      }
      return [];
    })();

    const values = {
      branches: branchesOptions.length === 1 ? [branchesOptions[0].value] : undefined,
      isShift: true,
      isDaily: false,
      employmentTypeId: 1,
      preferredClients: (data?.employeeClients || [])
        .filter(({ status }) => status === 0)
        .map(({ clientId }) => clientId),
      blockedClients: (data?.employeeClients || [])
        .filter(({ status }) => status === 1)
        .map(({ clientId }) => clientId),
      preferredAreaIds: data?.preferredAreaIds || [],
      ...defaultData,
      ...initialFieldsValue,
      workDays,
      wages: data?.wages || [],
      positionWages: data?.positionWages || [],
      primaryPositionIds,
      // Explicitly include isVerified and image from data to ensure they're in the form
      isVerified: data?.isVerified ?? false,
      image: data?.image ?? defaultData?.image,
      // Contract fields for ADD mode only
      ...(mode === 'ADD' && {
        contractId: undefined,
        contractStartDate: moment().format(API_DATE_FORMAT),
        // Contract adjustment fields (optional employee-specific overrides)
        contractMinimumWeeklyWorkingHours: undefined,
        contractMinimumHourlyRateOfPay: undefined,
        contractHolidayAccrualYearlyEntitlement: undefined,
        contractMaxDaysConsumeInWeek: undefined,
        contractSickDaysYearlyEntitlement: undefined
      })
    };

    return values;
  }, [
    branches,
    branchesOptions,
    data?.employeeClients,
    data?.preferredAreaIds,
    data?.wages,
    data?.positionWages,
    data?.isVerified,
    data?.image,
    data?.workDays,
    data?.primaryPositionIds,
    data?.accountsPositions,
    defaultData,
    initialFieldsValue,
    mode
  ]);

  const showPayrollTab = !!myPrivileges[privileges.PAYROLL];
  const allowEditProfile = !!myPrivileges[privileges.EDIT_HR];

  // Same gate as the /hiring route in App.js, so the tab never links to an unmounted route
  const { isRestrictedFromEmployees } = accounts.find(({ id }) => id === accountId) || {};
  const showHiringTab =
    mode !== 'ADD' && !!myPrivileges[privileges.ATS] && !!modules?.ats && !isRestrictedFromEmployees;

  const decorator = useMemo(() => getDecorator(branches), [branches]);

  const selectedBooking = useRecoilValue(selectedBookingModal);

  const onEditEmployeeFromSideBar = () => {
    onEditClickFromSideBar?.();
    setShowEditProfile(true);
  };

  const handleClose = useCallback(() => {
    if (pristineRef.current) {
      onClose();
    } else {
      setShowEscapeConfirm(true);
    }
  }, [onClose]);

  useEffect(() => {
    if (!showEditProfile) return;
    if (modalCallbacks.length === 0) return;

    if (myStackIndexRef.current === null) {
      myStackIndexRef.current = modalCallbacks.length - 1;
    }

    const onKeyDown = (e) => {
      if (e.key !== 'Escape') return;
      if (modalCallbacks.length - 1 !== myStackIndexRef.current) return;
      handleClose();
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [showEditProfile, modalCallbacks, handleClose]);

  const userDevice = useMemo(() => {
    const userAppVersions = (initialValues?.userDevices || []).map(({ appVersion }) => parseFloat(appVersion));
    const maxAppVersion = Math.max(...userAppVersions);
    return (initialValues?.userDevices || []).find(({ appVersion }) => maxAppVersion === parseFloat(appVersion));
  }, [initialValues?.userDevices]);

  const showLeaveManagementTab = useMemo(() => {
    if (!enableContractFeature || enableContractLeaveManagementFeature) return false;

    const contractType = data?.activeContract?.type;
    if (!contractType) return false;

    return contractType !== ACCOUNTS_CONTRACTS_TYPES.Casual;
  }, [enableContractFeature, enableContractLeaveManagementFeature, data?.activeContract]);

  const showLeaveManagementV2Tab =
    !!enableContractLeaveManagementFeature &&
    mode !== 'ADD' &&
    !!myPrivileges[privileges.MANAGE_EMPLOYEE_HOLIDAY_ACCRUAL];

  if (!showEditProfile) {
    return (
      <Form
        initialValues={initialValues}
        onSubmit={handleSubmit}
        render={(props) => (
          <ProfileSidebar
            {...props.initialValues}
            employeeFields={data?.fields || []}
            isLoading={isLoading}
            inviteEmployee={inviteEmployee}
            onClose={onClose}
            onEdit={onEditEmployeeFromSideBar}
            deleteEmployee={deleteEmployee}
            isSingleView={selectedBooking?.isSingleView}
          />
        )}
      />
    );
  }

  return (
    <>
      <FormModal
        dataTestId="employee-card-modal"
        isSingleView={selectedBooking?.isSingleView}
        size={modalSize}
        initialValues={initialValues}
        validate={({ documents }) => {
          if (!allowEditProfile) {
            return 'notValid';
          }
          if (documents?.length && !documents.every(({ typeId }) => typeId)) {
            return { documents: 'Missing type' };
          }
        }}
        zIndex={zIndex}
        decorators={[decorator]}
        disableLoadingConfirmButton={isLoading}
        loadingConfirm={isLoadingUpdateEmployee || isProcessingContract}
        onSubmit={handleSubmit}
        title={<CardTitle {...defaultData} mode={mode} />}
        onClose={onClose}
        confirmText={mode === 'ADD' ? 'add' : 'save'}
        showCancelButton={hideCancelButton ? false : mode === 'EDIT'}
        promptTitle="discardYourChanges"
        promptBody="unsavedChangesCloseBody"
        promptConfirmText="discardChanges"
        promptCancelText="keepEditing"
        isPaddingContent={false}
        requiredWarningFooter={allowEditProfile ? t('missingRequiredFields') : t('noEditEmployeeProfilePrivileges')}
        disabledOnPristine={false}
        enableReinitialize>
        {({ submitting, values, pristine }) => {
          pristineRef.current = pristine;
          return (
            <>
              <Tabs
                {...{
                  isLoading,
                  accountId,
                  activeTab,
                  weeklyPreference,
                  recurringAvailability,
                  setActiveTab,
                  mode,
                  handleClose: onClose,
                  inviteEmployee,
                  deleteEmployee,
                  ...initialValues,
                  updateEmployee,
                  isLoadingUpdateEmployee,
                  employeeFields: data?.fields || [],
                  rating: values.rating,
                  showPayrollTab,
                  showHiringTab,
                  showContractsTab: enableContractFeature && !enableContractLeaveManagementFeature,
                  showLeaveManagementTab,
                  showLeaveManagementV2Tab
                }}
              />

              <TabContent submitting={submitting} activeTab={activeTab}>
                <Profile
                  activeTab="profile"
                  isLoading={isLoading}
                  userId={initialValues?.userId}
                  userDevice={userDevice}
                  employeeId={initialValues.id}
                  addMode={mode === 'ADD'}
                  onClose={onClose}
                />
                {mode !== 'ADD' && <EmployeeAvailability activeTab="weeklyPreferences" employeeId={initialValues.id} />}

                <EmployeeGeneralAvailability
                  activeTab="generalAvailability"
                  employeeData={{ employeeId: initialValues.id, firstName: initialValues.firstName }}
                />

                {showPayrollTab && (
                  <Wages activeTab="wages" positions={values.positions} payrollFieldsByEmployee={employeeWages} />
                )}
                {activeTab === 'jobs' && <Job activeTab="jobs" data={initialValues} />}
                {activeTab === 'applicationsAndRejections' && (
                  <ApplicationsAndRejections activeTab="applicationsAndRejections" data={initialValues} />
                )}
                {showHiringTab && activeTab === 'hiringProcess' && (
                  <EmployeeHiring
                    activeTab="hiringProcess"
                    employeeId={initialValues.id}
                    accountId={accountId}
                    onNavigate={onClose}
                  />
                )}
                {activeTab === 'auditLogs' && <EmployeeAuditLogs activeTab="auditLogs" data={initialValues} />}
                {activeTab === 'comments' && <EmployeeComment activeTab="comments" data={initialValues} />}
                {activeTab === 'compliance' && (
                  <EmployeeCompliance activeTab="compliance" data={initialValues} employeeFields={data?.fields || []} />
                )}
                {enableContractFeature && !enableContractLeaveManagementFeature && activeTab === 'contracts' && (
                  <EmployeeContracts
                    accountRequiresContract={accountRequiresContract}
                    activeTab="contracts"
                    data={initialValues}
                    mode={mode}
                  />
                )}
                {showLeaveManagementTab && activeTab === 'contractTypes_leaveManagement' && (
                  <LeaveManagement activeTab="contractTypes_leaveManagement" data={initialValues} />
                )}
                {showLeaveManagementV2Tab && activeTab === 'leaveManagementV2' && (
                  <EmployeeLeaveManagementV2 activeTab="leaveManagementV2" data={initialValues} />
                )}
                {activeTab === 'workingTimeDirective' && (
                  <EmployeeWorkingTimeDirective activeTab="workingTimeDirective" data={initialValues} />
                )}
                {isUbeyaAdmin && activeTab === 'awr' && <EmployeeAWR activeTab="awr" data={initialValues} />}
                <TicketsHistoryWrapper activeTab="ticketsHistory">
                  <TicketsHistorySection
                    showTitle={true}
                    accountId={accountId}
                    employeeId={initialValues.id}
                    isActive={initialValues.isActive}
                    isDeleted={initialValues.isDeleted}
                  />
                </TicketsHistoryWrapper>
                <Documents activeTab="documents" employeeId={initialValues.id} />
                {weeklyPreference && <PersonalSettings activeTab="personalSettings" />}
              </TabContent>
            </>
          );
        }}
      </FormModal>
      {showEscapeConfirm && (
        <ConfirmationModal
          title="discardYourChanges"
          body="unsavedChangesCloseBody"
          confirmText="discardChanges"
          cancelText="keepEditing"
          onConfirm={onClose}
          onClose={() => setShowEscapeConfirm(false)}
          showCancelButton
          usePortal
          zIndex={(zIndex || 5) + 1}
        />
      )}
    </>
  );
};

export default EmployeeCard;
